import React, { useCallback } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useShouldUseRelayIssueHistory } from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-analytics/src/services/send-experience-analytics/index.tsx';
import { extractFirstErrorFromRelayError } from '@atlassian/jira-issue-view-layout-experience-tracking/src/field-experience-tracking/index.tsx';
import { TimezoneProvider } from './TimezoneProvider.tsx';
import type { Props } from './types.tsx';
import IssueHistory from './ui/index.tsx';
import IssueHistoryRelay from './ui/IssueHistory.tsx';
import { experienceDescription } from './services/issue-history/get-experience-tracking-details.tsx';

const IssueHistoryContainer = ({
	issueKey,
	timeZone,
	sortOrder,
	GroupedActivityEntryComponent,
	issueViewRelayFragment,
}: Props): React.JSX.Element => {
	const shouldUseRelayIssueHistory = useShouldUseRelayIssueHistory();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);

	const sendHistoryFetchAnalytics = useCallback(
		(error?: Error | null) => {
			const aggError = error ? extractFirstErrorFromRelayError(error) : null;
			const errorMessage = error ? aggError?.message || error?.message : undefined;
			sendExperienceAnalytics({
				error: aggError || error,
				getExperienceDescription: ({ wasSuccessful, statusCode }) =>
					experienceDescription({
						experience: 'issueHistory',
						action: 'FETCH_PAGE',
						analyticsSource: 'issueHistoryFetch',
						wasSuccessful,
						projectType,
						frameworkType: 'relay',
						...(errorMessage !== undefined ? { errorMessage } : null),
						...(statusCode !== undefined && { statusCode }),
					}),
			});
		},
		[projectType],
	);

	if (shouldUseRelayIssueHistory) {
		return (
			<JSErrorBoundary
				id="issue-history-relay"
				packageName="jiraIssueHistory"
				teamName="bento"
				onError={(location, error) => {
					sendHistoryFetchAnalytics(error);
				}}
			>
				<TimezoneProvider value={timeZone}>
					<IssueHistoryRelay
						issueKey={issueKey}
						sortOrder={sortOrder}
						GroupedActivityEntryComponent={GroupedActivityEntryComponent}
						onHistoryFetchAnalytics={sendHistoryFetchAnalytics}
					/>
				</TimezoneProvider>
			</JSErrorBoundary>
		);
	}

	return (
		<JSErrorBoundary id="issue-history">
			<TimezoneProvider value={timeZone}>
				<IssueHistory
					issueKey={issueKey}
					sortOrder={sortOrder}
					GroupedActivityEntryComponent={GroupedActivityEntryComponent}
					issueViewRelayFragment={issueViewRelayFragment}
				/>
			</TimezoneProvider>
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { default as HistoryItemComponent } from './ui/history-items/history-item';

export default IssueHistoryContainer;

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { default as HistoryItem } from './common/ui/history-item/index';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { useIssueHistoryUi } from './state/context';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { default as Change } from './common/ui/change';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { default as GroupedChange } from './common/ui/grouped-change';
